import { DeleteIcon } from '@chakra-ui/icons';
import {
  Badge,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useRef } from 'react';
import {
  GlobalPromoMediaType,
  PromoAutomationTemplate,
  TemplateNamespaceMapping,
} from 'src/api/v1-api';
import { LomaUploader } from 'src/components/molecules';
import { DEFAULT_IMAGE } from 'src/constants/config';
import { srcFromCDN } from 'src/utils/files';
import * as _ from 'lodash';
import { FilePath } from '../listing-form';
import { BiTrash } from 'react-icons/bi';
import DynamicFieldsGuide from './DynamicFieldsGuide';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import useRequest from 'src/utils/hooks/useRequest';

interface PromoAutomationTemplateFormProps {
  template: Partial<PromoAutomationTemplate>;
  globalPromoMediaTypeNamespaceMapping: TemplateNamespaceMapping | undefined;
  globalPromoMediaTypes: GlobalPromoMediaType[];
  updateTemplateValues: (template: Partial<PromoAutomationTemplate>) => void;
  previewPromoMedia: () => void;
  removeTemplate: () => void;
  canRemoveTemplate: boolean;
  hidePreview?: boolean;
}

const PromoAutomationTemplateForm = ({
  template,
  globalPromoMediaTypeNamespaceMapping,
  globalPromoMediaTypes,
  updateTemplateValues,
  previewPromoMedia,
  removeTemplate,
  canRemoveTemplate,
  hidePreview = false,
}: PromoAutomationTemplateFormProps) => {
  const { marketplace } = useMarketplaceContext();
  const { api } = useRequest();
  const { use_static_image, promo_image_url, global_promo_media_type_id, template_values } =
    template;

  const selectedGlobalPromoMediaType =
    globalPromoMediaTypes.find((g) => g.id === global_promo_media_type_id) ??
    globalPromoMediaTypes[0];

  const defaultTemplateValuesCache = useRef<Record<number, Record<string, any>>>({});

  useEffect(() => {
    // load and cache default template values for the promo listing form
    if (!hidePreview) return;
    if (!selectedGlobalPromoMediaType) return;
    if (!marketplace?.id) return;

    const mediaTypeId = selectedGlobalPromoMediaType.id;

    // If we already have cached defaults for this media type, apply them
    if (defaultTemplateValuesCache.current[mediaTypeId]) {
      updateTemplateValues({
        ...template,
        template_values: defaultTemplateValuesCache.current[mediaTypeId],
      });
      return;
    }

    // Otherwise, fetch from API and cache the result
    const fetchDefaultTemplateValues = async () => {
      try {
        const defaultTemplateValues =
          await api.shareautomation.promoAutomations.getDefaultTemplateValues(
            marketplace.id,
            mediaTypeId,
          );
        if (defaultTemplateValues) {
          defaultTemplateValuesCache.current[mediaTypeId] = defaultTemplateValues;
          updateTemplateValues({
            ...template,
            template_values: defaultTemplateValues,
          });
        }
      } catch (error) {
        console.error('Failed to load default template values:', error);
      }
    };
    fetchDefaultTemplateValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidePreview, marketplace?.id, selectedGlobalPromoMediaType?.id]);

  const getCustomizableFields: any = (template: any = {}) => {
    if (template.customizable) {
      if (!template.customizable.id) {
        return _.values(template.customizable);
      }
      return template.customizable;
    }

    if (_.isObject(template)) {
      return _.map(template, getCustomizableFields);
    }
  };

  const customizableTemplatebleFields = _.chain(
    getCustomizableFields(selectedGlobalPromoMediaType?.template),
  )
    .flattenDeep()
    .reject(_.isNil)
    .value();

  const onFileUpload = async (filePaths: FilePath[]) => {
    if (!updateTemplateValues || !filePaths[0]?.filePath) {
      return;
    }
    updateTemplateValues({ ...template, promo_image_url: filePaths[0]?.filePath });
  };

  const updateTemplateValue = (fieldId: string, value: string) => {
    updateTemplateValues({
      ...template,
      template_values: { ...template_values, [fieldId]: value },
    });
  };

  return (
    <Stack gap={4}>
      <Flex direction="column" mb={4}>
        <FormControl isRequired>
          <FormLabel m={0} mr={4}>
            Select Media Template
          </FormLabel>
        </FormControl>
      </Flex>
      <RadioGroup
        name="useStaticImage"
        value={use_static_image ? 'Use static image' : 'Use media template'}
        onChange={(value) => {
          updateTemplateValues({ ...template, use_static_image: value === 'Use static image' });
        }}
      >
        <Flex direction="column">
          <Radio value={'Use static image'} ml={{ base: 0, md: '0.5rem' }} mt={2}>
            Use static image
          </Radio>
          <Radio value="Use media template" ml={{ base: 0, md: '0.5rem' }} mt={2}>
            Use media template
          </Radio>
        </Flex>
      </RadioGroup>
      {use_static_image && (
        <Flex direction="column" gap={4}>
          {promo_image_url && (
            <Flex direction="column" gap={4} mb={12}>
              <Image
                src={promo_image_url}
                alt="Background Image"
                maxW="200px"
                fallbackSrc={DEFAULT_IMAGE}
              />
              <Button
                leftIcon={<DeleteIcon />}
                onClick={() => updateTemplateValues({ ...template, promo_image_url: '' })}
              >
                Remove image
              </Button>
            </Flex>
          )}
          <Flex direction="column" gap={4}>
            {promo_image_url && <Text>Upload new image</Text>}
            <LomaUploader
              maxFiles={1}
              onFileDelete={() => updateTemplateValues({ ...template, promo_image_url: '' })}
              onFileUpload={onFileUpload}
              filePathsLength={promo_image_url !== '' ? 1 : 0}
            />
          </Flex>
        </Flex>
      )}
      {!use_static_image && (
        <FormControl
          id="global_promo_media_type_id"
          isRequired
          gap={4}
          display="flex"
          flexDirection="column"
        >
          <Image
            src={srcFromCDN(selectedGlobalPromoMediaType?.sample_image_url, 200)}
            alt="Sample"
            maxW="200px"
            fallbackSrc={DEFAULT_IMAGE}
          />
          <RadioGroup
            name="global_promo_media_type_id"
            value={global_promo_media_type_id?.toString()}
            onChange={(value) => {
              updateTemplateValues({ ...template, global_promo_media_type_id: parseInt(value) });
            }}
          >
            <Stack
              gap={2}
              w="100%"
              flexWrap="wrap"
              direction={{ base: 'column', md: 'row' }}
              justifyContent="space-between"
            >
              {globalPromoMediaTypes
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((globalPromoMediaType, index) => (
                  <Radio
                    w={{ base: '100%', md: '45%' }}
                    key={globalPromoMediaType.id}
                    value={globalPromoMediaType.id.toString()}
                    defaultChecked={index === 0}
                    ml={{ base: 0, md: '0.5rem' }}
                    mt={2}
                  >
                    <Flex direction="row" alignItems="center" gap={2}>
                      {globalPromoMediaType.name}
                      <Badge p={1} rounded="lg" colorScheme="cyan">
                        {globalPromoMediaType.media_type}
                      </Badge>
                      <Badge p={1} rounded="lg" colorScheme="blue">
                        {globalPromoMediaType.entity_type_name}
                      </Badge>
                    </Flex>
                  </Radio>
                ))}
            </Stack>
          </RadioGroup>
          {customizableTemplatebleFields.length > 0 && (
            <Stack>
              <Flex direction={{ base: 'column-reverse', lg: 'row' }}>
                <Flex
                  direction="column"
                  gap={4}
                  minWidth={{ base: '100%', lg: '45%' }}
                  paddingRight={{ base: 0, lg: 8 }}
                >
                  <Text mt={4} fontSize="md" color="gray.500" fontWeight="bold">
                    Customize the template fields below
                  </Text>
                  {customizableTemplatebleFields.map(
                    (field: { id: string; label: string; type: string }) => (
                      <FormControl key={field.id}>
                        <FormLabel>{field.label}</FormLabel>
                        <Input
                          type={field.type}
                          value={template_values[field.id]}
                          onChange={(e) => updateTemplateValue(field.id, e.target.value)}
                        />
                      </FormControl>
                    ),
                  )}
                </Flex>
                <DynamicFieldsGuide
                  namespaceMapping={globalPromoMediaTypeNamespaceMapping}
                  paddingLeft={{ base: 0, lg: 8 }}
                  borderLeft={{ base: 'none', lg: '1px solid #D0D0D0' }}
                />
              </Flex>
            </Stack>
          )}
        </FormControl>
      )}
      <Flex direction="row" alignItems="center" justify="space-between">
        {selectedGlobalPromoMediaType?.media_type === 'image' &&
          !use_static_image &&
          !hidePreview && (
            <Button colorScheme="secondary" type="button" onClick={previewPromoMedia}>
              Preview
            </Button>
          )}
        {canRemoveTemplate && (
          <Button leftIcon={<BiTrash />} onClick={removeTemplate}>
            Remove
          </Button>
        )}
      </Flex>
    </Stack>
  );
};

export default PromoAutomationTemplateForm;
